import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BriefcaseIcon, CalendarIcon, CheckIcon, ClockIcon, GraduationCapIcon, LayersIcon, MapPinIcon, MonitorIcon, ShieldCheckIcon, TrendingUpIcon, UserIcon } from 'lucide-react';
import { getCareerRoleDetail, getDomain, getProgramHeroImage, getProgramMatch, getProgramsByDomain, programsIndexSlug, type ProgramTemplate } from '../data/programTemplates';
import { buildMatchLink } from '../utils/matchParams';
import { HeroMatchForm } from '../components/HeroMatchForm';
import { LegalFooter } from '../components/LegalFooter';
import { Logo } from '../components/Logo';
import { usePageMeta } from '../hooks/usePageMeta';
interface ProgramTemplatePageProps {
  program: ProgramTemplate;
}
export function ProgramTemplatePage({
  program
}: ProgramTemplatePageProps) {
  const domain = getDomain(program.domainSlug);
  const siblings = getProgramsByDomain(program.domainSlug);
  const related = siblings.filter(p => p.slug !== program.slug);
  const heroImage = getProgramHeroImage(program.slug);
  usePageMeta(program.metaTitle, program.metaDescription, {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [program.slug]);

  // Fallback path for visitors who scroll past the hero form.
  const matchHref = buildMatchLink('/request-information', getProgramMatch(program));
  const scrollToForm = () => {
    document.getElementById('hero-match-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const stats = [{
    icon: CalendarIcon,
    label: 'Program Length',
    value: `${program.weeks} weeks`
  }, {
    icon: LayersIcon,
    label: 'Credits',
    value: `${program.credits} credits`
  }, {
    icon: ClockIcon,
    label: 'Instructional Hours',
    value: program.instructionalHours.toLocaleString()
  }, {
    icon: MonitorIcon,
    label: 'Format',
    value: program.format
  }];
  return <main className="min-h-screen bg-white" data-id="element-2313" data-mp="34vfnqidvhanl">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-2314" data-mp="18rj6k8jujqv7">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-2315" data-mp="18t2nm1dbu5y1">
          <Link to="/" className="flex items-center" aria-label="findonlinecolleges.org home" data-id="element-2316" data-mp="1ioxf7lzj3bcy">
            <Logo data-id="element-2317" data-mp="1mqixxnyqujv2" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold tracking-wider uppercase text-navy" data-id="element-2318" data-mp="1mqr8vabb3f3m">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-crimson" aria-hidden="true" data-id="element-2319" data-mp="1rost3ykgxkgo" />
            Accredited Programs Only
          </div>
        </div>
      </div>

      {/* Hero. The photograph shows the work this program leads to; the navy
          base and scrim keep the headline legible while it loads and at widths
          where the image doesn't fully cover. */}
      <section className="relative bg-navy overflow-hidden" data-id="element-2320" data-mp="2agv2qpyp00hf">
        {heroImage && <>
            {/* Mirrored so the photo's subject sits on the left, in the open
                band beside the headline, instead of behind the form card. */}
            <img src={heroImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center lg:scale-x-[-1]" data-id="element-2321" data-mp="3t4finb63l3t" />
            <div className="absolute inset-0 bg-navy/80" aria-hidden="true" data-id="element-2322" data-mp="3qw0w9gl7hjbw" />
          </>}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-2323" data-mp="3lxpmf5uwv8to">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-2324" data-mp="mib1qkb66uud">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2325" data-mp="mht1lcldzwh6">
              <li data-id="element-2326" data-mp="ii0m0fxksq2b">
                <Link to={`/program-information/${programsIndexSlug}`} className="hover:text-white transition-colors duration-200" data-id="element-2327" data-mp="ihisvs67mrym">
                  New Online Programs
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2328" data-mp="8otkx79vbw81">/</li>
              <li className="text-white/90" data-id="element-2329" data-mp="166jw9106rcx1">{domain?.name}</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-2330" data-mp="11f1u49mff6ah">
            <div className="lg:col-span-3" data-id="element-2331" data-mp="l02gah4wb74c">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold uppercase tracking-widest text-gold mb-5" style={{
              color: "#E3924E"
            }} data-id="element-2332" data-mp="kzaw02gr0vtd">
                <GraduationCapIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2333" data-mp="c18nz2yll2wl" />
                {program.credential}
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-5" data-id="element-2334" data-mp="14obizl42evi3">
                {program.heroHeadline}
              </h1>
              <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-2335" data-mp="18p56iddli8ag">
                {program.heroSubhead}
              </p>
              <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80" data-id="element-2336" data-mp="uwfhs16omp9n">
                <li className="flex items-center gap-2" data-id="element-2337" data-mp="zv8lj1w631wm">
                  <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2338" data-mp="1nlw6oov3fo14" />
                  {program.format}
                </li>
                <li className="flex items-center gap-2" data-id="element-2339" data-mp="1sfh0e4pnlvui">
                  <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2340" data-mp="21r4wkdp9re29" />
                  {program.weeks} weeks
                </li>
                <li className="flex items-center gap-2" data-id="element-2341" data-mp="2bhzkrnm2snqc">
                  <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2342" data-mp="2bhr3ljix6w9c" />
                  No cost to get matched
                </li>
              </ul>
            </div>

            <div id="hero-match-form" className="lg:col-span-2" data-id="element-2343" data-mp="1iu3qepjzb7wq">
              <HeroMatchForm programs={siblings.length > 0 ? siblings : [program]} initialProgramSlug={program.slug} data-id="element-2344" data-mp="1esi98s0ew7gq" />
            </div>
          </div>
        </div>
      </section>

      {/* Stat row */}
      <section className="border-b border-gray-200 bg-light" data-id="element-2345" data-mp="1nsezuz6d3gef">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2346" data-mp="1nv16mwlfhrvl">
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2347" data-mp="345dcn7ax2t3l">
            {stats.map(stat => <div key={stat.label} className="flex items-start gap-3" data-id="element-2348" data-mp="2z7218s4z4aj9">
                <stat.icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-crimson" aria-hidden="true" data-id="element-2349" data-mp="3792ot0ets85q" />
                <div data-id="element-2350" data-mp="3b9d3d1goo6w7">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-2351" data-mp="3b8tipcznf4eh">
                    {stat.label}
                  </dt>
                  <dd className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-2352" data-mp="3l82b41ev8ayq">
                    {stat.value}
                  </dd>
                </div>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2353" data-mp="2skojmitxs7m0">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-2354" data-mp="2xizumwvywo5t">
          <div className="lg:col-span-2 space-y-12" data-id="element-2355" data-mp="2xhf0379nl57y">
            <section aria-labelledby="overview-heading" data-id="element-2356" data-mp="329yg0avtccfx">
              <h2 id="overview-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2357" data-mp="32bj9ryaazrcq">
                Program Overview
              </h2>
              <div className="space-y-4" data-id="element-2358" data-mp="29slyova9i55y">
                {program.overview.map((paragraph, idx) => <p key={idx} className="text-gray-700 leading-relaxed" data-id="element-2359" data-mp="229cr97lbggjj" data-mpi={idx}>
                    {paragraph}
                  </p>)}
              </div>
            </section>

            {program.concentrations && <section aria-labelledby="concentrations-heading" data-id="element-2360" data-mp="1sbgkst3bq9rr">
                <h2 id="concentrations-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2361" data-mp="1s9lx570o43jz">
                  Program Concentrations
                </h2>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-2362" data-mp="1jb0g5gaugwlb">
                  {program.concentrations.map(c => <div key={c.name} className="border border-gray-200 rounded-xl p-5 flex flex-col" data-id="element-2363" data-mp="1ejiib144bc4k">
                      <h3 className="font-bold text-navy mb-2" data-id="element-2364" data-mp="19k5uoe5fa10x">{c.name}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2365" data-mp="14m4b7s433zox">
                        {c.description}
                      </p>
                    </div>)}
                </div>
              </section>}

            <section aria-labelledby="features-heading" data-id="element-2366" data-mp="vko0fj9cxvk6">
              <h2 id="features-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2367" data-mp="vnajaf1p7xhp">
                Unique Program Features
              </h2>
              <ul className="space-y-3" data-id="element-2368" data-mp="2ohi5nh3vbvyn">
                {program.features.map(feature => <li key={feature} className="flex items-start" data-id="element-2369" data-mp="29jpt8xtmbnoo">
                    <CheckIcon className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-crimson" aria-hidden="true" data-id="element-2370" data-mp="29jg41cgedkk1" />
                    <span className="text-gray-700" data-id="element-2371" data-mp="2ekvnj02xfj0b">{feature}</span>
                  </li>)}
              </ul>
            </section>

            {program.studentProfile && <section aria-labelledby="profile-heading" data-id="element-2372" data-mp="25j5xnyop0s4n">
                <h2 id="profile-heading" className="text-2xl font-bold text-navy mb-4" data-id="element-2373" data-mp="1qt85l7nn7ia6">
                  Who This Program Is For
                </h2>
                <div className="border-l-4 border-crimson pl-5" data-id="element-2374" data-mp="1vt2x9df8sbwa">
                  <p className="text-gray-700 leading-relaxed" data-id="element-2375" data-mp="1hs07apl4mhg3">
                    {program.studentProfile}
                  </p>
                </div>
              </section>}

          </div>

          {/* Sidebar. Sticky so it travels with the reader instead of leaving a
              tall empty column beside the long-form content. */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start" data-id="element-2376" data-mp="y8b2wg5rh7k6">
            <div className="border border-gray-200 rounded-2xl p-6 bg-light" data-id="element-2377" data-mp="1c6rikwllq8eg">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2378" data-mp="177odjc5mg26h">
                At a Glance
              </h2>
              <dl className="space-y-3 text-sm" data-id="element-2379" data-mp="1m3e2nj63svff">
                <div className="flex justify-between gap-4" data-id="element-2380" data-mp="1h5m4i7ev8d2s">
                  <dt className="text-gray-500" data-id="element-2381" data-mp="1h5c9g5eyrfq8">Credential</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2382" data-mp="1ux90e64wbjl1">
                    {program.credential}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2383" data-mp="1qxrsz75wscd8">
                  <dt className="text-gray-500" data-id="element-2384" data-mp="24vyf5q1hwnx2">Length</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2385" data-mp="1zwc1xglo37o9">
                    {program.weeks} weeks
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2386" data-mp="2k31nhk45ipgx">
                  <dt className="text-gray-500" data-id="element-2387" data-mp="2f9yzdri58884">Credits</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2388" data-mp="2b9g5lrwows3a">
                    {program.credits}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2389" data-mp="26bws4u7tgjce">
                  <dt className="text-gray-500" data-id="element-2390" data-mp="3314fm7rzx2iy">Hours</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2391" data-mp="32yi7o7170l07">
                    {program.instructionalHours.toLocaleString()}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2392" data-mp="2tyd0nxzwvomk">
                  <dt className="text-gray-500" data-id="element-2393" data-mp="2p1mldcvxkx27">Program</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2394" data-mp="2p37fx2i8wg02">
                    {domain?.name}
                  </dd>
                </div>
              </dl>
              <button type="button" onClick={scrollToForm} className="mt-6 w-full bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm uppercase tracking-wide transition-colors duration-200" style={{
              backgroundColor: "#E3924E"
            }} data-id="element-2395" data-mp="1dk3cp4zl01w9">
                Get Matched
              </button>
            </div>

            {related.length > 0 && <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2396" data-mp="1i6nixjfrud72">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2397" data-mp="1i8qfkmbfj4kr">
                  Also in This Domain
                </h2>
                <ul className="space-y-3" data-id="element-2398" data-mp="1r6iwfuwsuka9">
                  {related.map(p => <li key={p.slug} data-id="element-2399" data-mp="1w6f1lxh8ghre">
                      <Link to={`/program-information/${p.slug}`} className="group flex items-start justify-between gap-3" data-id="element-2400" data-mp="1w7gdg462p8a6">
                        <span className="text-sm text-gray-700 group-hover:text-navy transition-colors duration-200" data-id="element-2401" data-mp="214zsv7eibr3p">
                          {p.name}
                          <span className="block text-xs text-gray-500" data-id="element-2402" data-mp="6qysgi3b6w1i">
                            {p.credential}
                          </span>
                        </span>
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-2403" data-mp="3px3o5cictd6r" />
                      </Link>
                    </li>)}
                </ul>
              </div>}

            <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2404" data-mp="3oatayzz8djq">
              <UserIcon className="w-6 h-6 mb-3 text-crimson" aria-hidden="true" data-id="element-2405" data-mp="hovd9yhmofet" />
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2406" data-mp="cqtr34ownzz4">
                Not sure which program fits? Compare all six program domains and
                find the pathway that matches your goals.
              </p>
              <Link to={`/program-information/${programsIndexSlug}`} className="mt-4 inline-flex items-center text-sm font-semibold text-crimson hover:underline" data-id="element-2407" data-mp="mheowpwr90gz">
                Browse all programs
                <ArrowRightIcon className="w-4 h-4 ml-1.5" aria-hidden="true" data-id="element-2408" data-mp="wfkoh0v39uio" />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Careers. Full width, because the role cards need the room — a bare
          list of job titles tells a career changer nothing about whether the
          role suits them. */}
      <section aria-labelledby="careers-heading" className="border-t border-gray-200 bg-light" data-id="element-2409" data-mp="164hwuawisplx">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2410" data-mp="1b3cmnwswnefj">
          <div className="max-w-3xl" data-id="element-2411" data-mp="wasprdwcm36j">
            <div className="flex items-baseline justify-between gap-4 mb-4" data-id="element-2412" data-mp="1132amfilwd1y">
              <h2 id="careers-heading" className="text-2xl font-bold text-navy" data-id="element-2413" data-mp="1155b5thdxokx">
                Careers to Explore
              </h2>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap" data-id="element-2414" data-mp="rcziehjb734o">
                {program.careerRoles.length} roles
              </span>
            </div>
            <p className="text-gray-700 leading-relaxed mb-8" data-id="element-2415" data-mp="icb2tie9oupo">
              {program.careersIntro}
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2416" data-mp="1kop8j7c5c62r">
            {program.careerRoles.map(role => {
            const detail = getCareerRoleDetail(role);
            return <li key={role} className="flex" data-id="element-2417" data-mp="1bx5id9lkr4je">
                  <div className="w-full bg-white border border-gray-200 rounded-xl p-5 flex flex-col hover:border-navy transition-colors duration-200" data-id="element-2418" data-mp="1bvkkpb3yr5hb">
                    <div className="flex items-start gap-3" data-id="element-2419" data-mp="11zrahydpvo2p">
                      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-light flex items-center justify-center text-navy" aria-hidden="true" data-id="element-2420" data-mp="11x4vjdohy6ag">
                        <BriefcaseIcon className="w-4 h-4" data-id="element-2421" data-mp="szuf7dwtnqyo" />
                      </span>
                      <h3 className="text-sm font-bold text-navy leading-snug pt-1.5" data-id="element-2422" data-mp="o0hwn5czh61w">
                        {role}
                      </h3>
                    </div>
                    {detail && <>
                        <p className="mt-3 text-sm text-gray-600 leading-relaxed" data-id="element-2423" data-mp="2iesm9g1ek48q">
                          {detail.summary}
                        </p>
                        <p className="mt-auto pt-4 flex items-center gap-1.5 text-xs text-gray-500" data-id="element-2424" data-mp="2pvfk6yjwd3aa">
                          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0 text-crimson" aria-hidden="true" data-id="element-2425" data-mp="2zn384ph8w270" />
                          {detail.setting}
                        </p>
                      </>}
                  </div>
                </li>;
          })}
          </ul>

          <p className="mt-6 max-w-3xl text-xs text-gray-500 leading-relaxed" data-id="element-2426" data-mp="2708otvw5z8v4">
            Roles listed are examples of where graduates commonly begin. Actual
            hiring, pay, and advancement vary by employer, location, and
            experience — check current openings and Bureau of Labor Statistics
            data for your area.
          </p>

          {program.marketRelevance && <div className="mt-10 pt-10 border-t border-gray-200 max-w-3xl" data-id="element-2427" data-mp="3sb7frgjdmpcc">
              <h2 className="text-xl font-bold text-navy mb-3 flex items-center gap-2" data-id="element-2428" data-mp="6wul0vak4l4">
                <TrendingUpIcon className="w-5 h-5 text-crimson" aria-hidden="true" data-id="element-2429" data-mp="1x7sz3os1btrr" />
                Market Relevance
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-2430" data-mp="1s8zn60secatp">
                {program.marketRelevance}
              </p>
            </div>}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-navy py-14" data-id="element-2431" data-mp="21apaauf8wnlo">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2432" data-mp="1dkl8mslmaxxb">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2433" data-mp="1diijsbw7bau6">
            Ready to Take the Next Step?
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-2434" data-mp="1naehhludkyxv">
            Get matched with accredited schools offering the {program.name}{' '}
            {program.credential.toLowerCase()} program. No cost, and no
            obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2435" data-mp="ussozosfup29">
            <button type="button" onClick={scrollToForm} className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-10 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200" style={{
            backgroundColor: "#E3924E"
          }} data-id="element-2436" data-mp="ptzd20ssv647">
              Get Matched
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2437" data-mp="2siqtedb19enq" />
            </button>
            
          </div>
        </div>
      </section>

      <LegalFooter data-id="element-2438" data-mp="2xeei6rlls5sp" />
    </main>;
}